import { initializeApp } from "firebase/app";
import { createUserWithEmailAndPassword, getAuth, sendPasswordResetEmail } from "firebase/auth";
import { collection, deleteDoc, doc, getDocs, query, setDoc, where } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Edit2,
  Eye,
  EyeOff,
  Mail,
  Plus,
  Search,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

// Create a separate Firebase app instance for partner creation
const partnerAuthApp = initializeApp( 
  {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  },
  "partnerAuth"
);

// Get auth instance for partner creation
const partnerAuth = getAuth(partnerAuthApp);

interface Partner {
  id: string;
  userId: string;
  maxCoaches: number;
  stats: {
    totalCoaches: number;
    totalRespondents: number;
  };
  user: {
    fullName: string;
    email: string;
  };
}

const columnHelper = createColumnHelper<Partner>();

export function PartnerManagement() {
  const navigate = useNavigate(); /* @ts-ignore */
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPartner, setNewPartner] = useState({
    email: "",
    fullName: "",
    password: "",
    confirmPassword: "",
    maxCoaches: 5,
    sendResetEmail: false,
    permissions: {
      manageCoaches: true,
      createAssessmentCodes: true,
    },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [coachesFilter, setCoachesFilter] = useState("all");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<Partner | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns = useMemo(() => [
    columnHelper.accessor('user.fullName', {
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('user.email', {
      header: 'Email',
      cell: (info) => (
        <div className="text-gray-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('maxCoaches', {
      header: 'Max Coaches',
      cell: (info) => (
        <div className="text-gray-900 font-medium">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('stats.totalCoaches', {
      header: 'Total Coaches',
      cell: (info) => (
        <div className="text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('stats.totalRespondents', {
      header: 'Total Respondents',
      cell: (info) => (
        <div className="text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const partner = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {}}
              className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
              title="Edit Partner"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setPartnerToDelete(partner);
                setShowDeleteModal(true);
              }}
              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all"
              title="Delete Partner"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
    }),
  ], []);

  const filteredData = useMemo(() => {
    return partners.filter(partner => {
      // Coaches filter
      if (coachesFilter === "low" && partner.stats.totalCoaches > 2) {
        return false;
      }
      if (coachesFilter === "medium" && (partner.stats.totalCoaches <= 2 || partner.stats.totalCoaches > 10)) {
        return false;
      }
      if (coachesFilter === "high" && partner.stats.totalCoaches <= 10) {
        return false;
      }
      
      return true;
    });
  }, [partners, coachesFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    if (auth?.currentUser?.uid) {
      loadPartners();
    }
  }, [auth?.currentUser?.uid]);

  const loadPartners = async () => {
    try {
      setIsLoading(true);
      const partnersQuery = query(collection(db, "partners"));
      const partnersSnapshot = await getDocs(partnersQuery);

      const partnersData = await Promise.all(
        partnersSnapshot.docs.map(async (doc) => {
          const data = doc.data();

          // Get user data
          const userDoc = await getDocs(
            query(collection(db, "users"), where("userId", "==", data.userId))
          );
          const userData = userDoc.docs[0]?.data();

          // Get coaches count
          const coachesQuery = query(
            collection(db, "coaches"),
            where("partnerId", "==", data.userId)
          );
          const coachesSnapshot = await getDocs(coachesQuery);
          const coachIds = coachesSnapshot.docs.map((doc) => doc.data().userId);

          // Get total respondents only if there are coaches
          let respondentsCount = 0;
          if (coachIds.length > 0) {
            // Split coachIds into chunks of 30 to avoid Firestore 'in' query limit
            const chunkSize = 30;
            for (let i = 0; i < coachIds.length; i += chunkSize) {
              const chunk = coachIds.slice(i, i + chunkSize);
              const respondentsQuery = query(
                collection(db, "respondents"),
                where("coachId", "in", chunk)
              );
              const respondentsSnapshot = await getDocs(respondentsQuery);
              respondentsCount += respondentsSnapshot.size;
            }
          }

          return {
            id: doc.id,
            userId: data.userId,
            maxCoaches: data.maxCoaches,
            stats: {
              totalCoaches: coachesSnapshot.size,
              totalRespondents: respondentsCount,
            },
            user: {
              fullName: userData?.fullName || "",
              email: userData?.email || "",
            },
          };
        })
      );

      setPartners(partnersData);
    } catch (err) {
      console.error("Error loading partners:", err);
      setError("Failed to load partners");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPartner = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newPartner.sendResetEmail) {
      if (newPartner.password.length < 6) {
        setError("Password must be at least 6 characters");
        return;
      }
      if (newPartner.password !== newPartner.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
    }

    try {
      setIsLoading(true);
      const adminId = auth.currentUser?.uid;

      const adminQuery = query(
        collection(db, "users"),
        where("userId", "==", adminId),
        where("role", "==", "admin")
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (adminSnapshot.empty) {
        throw new Error("Insufficient permissions");
      }

      const passwordToUse = newPartner.sendResetEmail
        ? `Temp${Date.now()}!Aa`
        : newPartner.password;

      const { user: partnerUser } = await createUserWithEmailAndPassword(
        partnerAuth,
        newPartner.email,
        passwordToUse
      );

      const now = new Date();
      await setDoc(doc(db, "user_agreements", partnerUser.uid), {
        userId: partnerUser.uid,
        privacyAccepted: true,
        termsAccepted: true,
        disclaimerAccepted: true,
        gdprAccepted: true,
        privacyAcceptedAt: now,
        termsAcceptedAt: now,
        disclaimerAcceptedAt: now,
        gdprAcceptedAt: now,
        updatedAt: now,
      });

      await setDoc(doc(db, "users", partnerUser.uid), {
        userId: partnerUser.uid,
        email: newPartner.email,
        fullName: newPartner.fullName,
        role: "partner",
        subscription_tier: "partner",
        createdAt: now,
        updatedAt: now,
      });

      await setDoc(doc(db, "partners", partnerUser.uid), {
        userId: partnerUser.uid,
        maxCoaches: newPartner.maxCoaches,
        permissions: newPartner.permissions,
        createdAt: now,
        updatedAt: now,
      });

      await partnerAuth.signOut();

      if (newPartner.sendResetEmail) {
        await sendPasswordResetEmail(auth, newPartner.email);
      }

      setShowAddModal(false);
      setNewPartner({
        email: "",
        fullName: "",
        password: "",
        confirmPassword: "",
        maxCoaches: 5,
        sendResetEmail: false,
        permissions: {
          manageCoaches: true,
          createAssessmentCodes: true,
        },
      });
      loadPartners();
    } catch (err: any) {
      console.error("Error adding partner:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists");
      } else {
        setError("Failed to add partner");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;

    try {
      setIsDeleting(true);
      setError("");

      const userId = partnerToDelete.userId;

      await deleteDoc(doc(db, "partners", userId));
      await deleteDoc(doc(db, "users", userId));
      await deleteDoc(doc(db, "user_agreements", userId));

      setShowDeleteModal(false);
      setPartnerToDelete(null);
      loadPartners();
    } catch (err) {
      console.error("Error deleting partner:", err);
      setError("Failed to delete partner data. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading partners..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Users className="w-12 h-12 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Partner Management
              </h1>
              <p className="text-gray-600">
                Create and manage partner accounts
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r 
              from-blue-500 to-indigo-600 text-white font-medium hover:scale-105 transition-all"
          >
            <Plus className="w-5 h-5" />
            Add Partner
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-gray-200">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search partners..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              />
            </div>
            
            <div>
              <select
                value={coachesFilter}
                onChange={(e) => setCoachesFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All Partners</option>
                <option value="low">1-2 Coaches</option>
                <option value="medium">3-10 Coaches</option>
                <option value="high">10+ Coaches</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {partners.length === 0 ? "No partners found" : "No partners match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-gray-600">of {table.getPageCount()}</span>
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
                
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Add Partner Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 w-full max-w-lg mx-4 shadow-xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Add New Partner
              </h2>
              <form onSubmit={handleAddPartner} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-gray-900 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={newPartner.fullName}
                    onChange={(e) =>
                      setNewPartner((prev) => ({
                        ...prev,
                        fullName: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newPartner.email}
                    onChange={(e) =>
                      setNewPartner((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  />
                </div>

                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="sendResetEmail"
                    checked={newPartner.sendResetEmail}
                    onChange={(e) =>
                      setNewPartner((prev) => ({
                        ...prev,
                        sendResetEmail: e.target.checked,
                        password: "",
                        confirmPassword: "",
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="sendResetEmail" className="text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Send password reset email (partner sets their own password)
                  </label>
                </div>

                {!newPartner.sendResetEmail && (
                  <>
                    <div>
                      <label htmlFor="password" className="block text-gray-900 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          value={newPartner.password}
                          onChange={(e) =>
                            setNewPartner((prev) => ({
                              ...prev,
                              password: e.target.value,
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300
                            focus:border-blue-500 focus:outline-none text-gray-900 pr-12"
                          required={!newPartner.sendResetEmail}
                          minLength={6}
                          placeholder="Minimum 6 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-gray-900 mb-2">
                        Confirm Password
                      </label>
                      <input
                        type={showPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={newPartner.confirmPassword}
                        onChange={(e) =>
                          setNewPartner((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300
                          focus:border-blue-500 focus:outline-none text-gray-900"
                        required={!newPartner.sendResetEmail}
                        placeholder="Re-enter password"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="maxCoaches" className="block text-gray-900 mb-2">
                    Maximum Coaches
                  </label>
                  <input
                    type="number"
                    id="maxCoaches"
                    min="1"
                    value={newPartner.maxCoaches}
                    onChange={(e) =>
                      setNewPartner((prev) => ({
                        ...prev,
                        maxCoaches: parseInt(e.target.value),
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                      focus:border-blue-500 focus:outline-none text-gray-900"
                    required
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="manageCoaches"
                      checked={newPartner.permissions.manageCoaches}
                      onChange={(e) =>
                        setNewPartner((prev) => ({
                          ...prev,
                          permissions: {
                            ...prev.permissions,
                            manageCoaches: e.target.checked,
                          },
                        }))
                      }
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="manageCoaches" className="text-gray-900">
                      Can manage coaches
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="createCodes"
                      checked={newPartner.permissions.createAssessmentCodes}
                      onChange={(e) =>
                        setNewPartner((prev) => ({
                          ...prev,
                          permissions: {
                            ...prev.permissions,
                            createAssessmentCodes: e.target.checked,
                          },
                        }))
                      }
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="createCodes" className="text-gray-900">
                      Can create assessment codes
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 
                      text-white font-medium hover:scale-105 transition-all disabled:opacity-50
                      flex items-center gap-2"
                  >
                    <Shield className="w-5 h-5" />
                    {isLoading ? "Adding..." : "Add Partner"}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showDeleteModal && partnerToDelete && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 w-full max-w-md mx-4 shadow-xl"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Delete Partner
                  </h2>
                  <p className="text-gray-600">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-gray-700 mb-2">
                  You are about to delete:
                </p>
                <p className="font-semibold text-gray-900">{partnerToDelete.user.fullName}</p>
                <p className="text-gray-600 text-sm">{partnerToDelete.user.email}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> This will remove the partner's data from the database.
                  You'll also need to delete them from Firebase Authentication in the Firebase Console.
                </p>
              </div>

              {partnerToDelete.stats.totalCoaches > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                  <p className="text-red-800 text-sm">
                    <strong>Warning:</strong> This partner has {partnerToDelete.stats.totalCoaches} coach(es)
                    and {partnerToDelete.stats.totalRespondents} respondent(s) associated with them.
                    These will become orphaned after deletion.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPartnerToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePartner}
                  disabled={isDeleting}
                  className="px-6 py-3 rounded-xl bg-red-600 text-white font-medium
                    hover:bg-red-700 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  {isDeleting ? "Deleting..." : "Delete Partner"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}